import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react"
import { useAuth as useClerkAuth } from "@clerk/react"
import { configureTokenGetter } from "@/services/api.client"
import { useAuthValue } from "@/features/auth/hooks/useAuth"
import { identifyUser, resetUser, trackSignedIn, trackSignedOut } from "@/lib/analytics"
import { useMeStore } from "@/features/auth/hooks/useCurrentUser"
import { useActiveOrgStore } from "@/features/auth/org.store"
import { AuthContext } from "./AuthProvider"

/** Confidential case content is per-officer; purge app-scoped keys on sign-out (shared workstations). */
function purgeLocalCaseData() {
  try {
    const stale: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith("oio-") || key.startsWith("qa-"))) stale.push(key)
    }
    stale.forEach((key) => localStorage.removeItem(key))
  } catch {
    // Storage disabled/full: a failed purge must not block sign-out.
  }
}

function AuthContextFeeder({ children }: { children: ReactNode }) {
  const value = useAuthValue()
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function ClerkAuthBridge({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken, userId } = useClerkAuth()

  // Layout effect so children's /me fetch (a passive effect) always sees the getter.
  useLayoutEffect(() => {
    configureTokenGetter((opts) => getToken(opts))
  }, [getToken])

  // A different user on the same tab: drop the previous user's /me and org.
  const prevUserId = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!isLoaded) return
    const prev = prevUserId.current
    prevUserId.current = userId ?? null
    if (prev && userId && prev !== userId) {
      useMeStore.getState().reset()
      useActiveOrgStore.getState().setActiveOrgId(null)
    }
  }, [isLoaded, userId])

  // Identify with our own user id once /me loads (never the Clerk id).
  const me = useMeStore((s) => s.me)
  useEffect(() => {
    if (isSignedIn && me) {
      identifyUser(me)
      trackSignedIn(me.user_id)
    }
  }, [isSignedIn, me])

  const wasSignedIn = useRef(false)
  useEffect(() => {
    if (!isLoaded) return
    if (isSignedIn) {
      wasSignedIn.current = true
      return
    }
    if (wasSignedIn.current) {
      wasSignedIn.current = false
      trackSignedOut()
      resetUser()
      purgeLocalCaseData()
    }
    useMeStore.getState().reset()
    useActiveOrgStore.getState().setActiveOrgId(null)
  }, [isLoaded, isSignedIn])

  return <AuthContextFeeder>{children}</AuthContextFeeder>
}
